/**
 * OnboardingRedirect — iter117
 *
 * Replaces the legacy OnboardingHubPage at the `/onboarding` route. Owners
 * never see a "hub" page anymore — they always land directly on the step
 * they need to complete next.
 *
 * Flow:
 *   1. Not authenticated → /onboarding/account (Step 1).
 *   2. Authenticated → GET /api/onboarding/state → route to the matching
 *      step page (Plan / Stripe / Go live) or /dashboard when done.
 *
 * This component renders only a brief loading state; the actual UI is the
 * destination page.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_ROUTES = {
  account_created: '/onboarding/account',
  choose_plan:     '/onboarding/plan',
  stripe_connect:  '/onboarding/stripe-connect',
  setup:           '/onboarding/setup',
  // Legacy keys (any persisted cursor from earlier iterations) collapse to
  // the closest visible step so existing users never get stranded.
  payout_trust:    '/onboarding/plan',
  subscribe:       '/onboarding/setup',
};

export default function OnboardingRedirect() {
  const navigate = useNavigate();
  const { token } = useAuth() || {};

  useEffect(() => {
    let cancelled = false;
    const go = async () => {
      // Unauthenticated → Step 1 signup form.
      if (!token) {
        navigate('/onboarding/account', { replace: true });
        return;
      }
      try {
        const r = await axios.get(`${API}/api/onboarding/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const step = r?.data?.step;
        if (cancelled) return;
        if (step === 'done') {
          navigate('/dashboard', { replace: true });
          return;
        }
        const target = STEP_ROUTES[step] || '/onboarding/account';
        navigate(target, { replace: true });
      } catch {
        // If the state lookup fails, send the owner to the start of the
        // wizard rather than stranding them — they can still navigate
        // forward via the stepper.
        if (!cancelled) navigate('/onboarding/account', { replace: true });
      }
    };
    go();
    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <div
      className="min-h-screen bg-zinc-950 flex items-center justify-center text-white/60"
      data-testid="onboarding-redirect"
    >
      <Loader2 className="w-5 h-5 animate-spin mr-3 text-yellow-300" />
      <span className="text-[13px] tracking-wide">Loading your next step…</span>
    </div>
  );
}
